import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DateBox from 'devextreme-react/date-box';
import {
    PermissionType,
    WatchlistCategoryType,
    WatchlistCategoryTypeOptions,
    WatchlistCureType,
    WatchlistCureTypeOptions,
} from '@apply/constants';
import { hasPermission } from '@apply/ui';

export const WatchlistIssueModal = ({ issue, onSubmit, onCancel, title }) => {
    const createDate = (dt) => {
        if (!dt) {
            return null;
        }
        const dtOut = new Date(dt);
        dtOut.setHours(12, 0, 0, 0);
        return dtOut;
    };

    const today = createDate(new Date());

    const [item, set_item] = useState(null);
    const [issueDate, set_issueDate] = useState(today);
    const [categoryTypeId, set_categoryTypeId] = useState(null);
    const [otherCategoryName, set_otherCategoryName] = useState('');
    const [curedDate, set_curedDate] = useState(null);
    const [cureTypeId, set_cureTypeId] = useState(null);
    const [otherCureName, set_otherCureName] = useState('');
    const [isOutstanding, set_isOutstanding] = useState(false);
    const [closedDate, set_closedDate] = useState(null);
    const [allowedCategories, set_allowedCategories] = useState([]);

    const styles = {
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { padding: '10px 5px' },
        formRow: { marginBottom: '0' },
    };

    const handleClose = () => {
        onCancel && onCancel();
    };

    const onFormSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const model = {
            guid: issue.guid,
            issueDate: issueDate ? new Date(issueDate).toISOString().split('T')[0] : null,
            categoryTypeId,
            otherCategoryName,
            cureTypeId,
            curedDate: curedDate ? new Date(curedDate).toISOString().split('T')[0] : null,
            otherCureName,
            isOutstanding,
            closedDate: closedDate ? new Date(closedDate).toISOString().split('T')[0] : null,
        };

        onSubmit && onSubmit(model);
    };

    useEffect(() => {
        if (issue) {
            setState(issue);
        } else {
            resetState();
        }
    }, [issue]);

    const setState = (issue) => {
        set_issueDate(createDate(issue.issueDate ? new Date(issue.issueDate) : today));
        set_allowedCategories(issue.allowedCategories);
        set_item(issue);

        let catg = issue.allowedCategories.find((c) => c.id === issue.categoryTypeId);
        if (!catg && issue.allowedCategories.length > 0) {
            catg = issue.allowedCategories[0];
        }
        set_categoryTypeId(catg?.id);
        set_otherCategoryName(issue.otherCategoryName);
        set_curedDate(createDate(issue.curedDate ? new Date(issue.curedDate) : null));
        set_cureTypeId(issue.cureTypeId);
        set_otherCureName(issue.otherCureName);
        set_isOutstanding(issue.isOutstanding);
        set_closedDate(createDate(issue.closedDate ? new Date(issue.closedDate) : null));
    };

    const resetState = () => {
        set_issueDate(today);
        set_allowedCategories([]);
        set_item(null);
        set_categoryTypeId(null);
        set_otherCategoryName('');
        set_curedDate(null);
        set_cureTypeId(null);
        set_otherCureName('');
        set_isOutstanding(false);
        set_closedDate(null);
    };

    const canSubmit = () => {
        if (isLocked()) {
            return false;
        }
        if (!categoryTypeId) {
            return false;
        }
        if (categoryTypeId === WatchlistCategoryType.Other && !otherCategoryName) {
            return false;
        }

        if (cureTypeId == WatchlistCureType.Other && !otherCureName) {
            return false;
        }

        if (cureTypeId && !curedDate) {
            return false;
        }

        if (curedDate && !isOutstanding && !closedDate) {
            return false;
        }

        return true;
    };

    const getAllowedCures = (catgId) => {
        let cureTypes = [];
        switch (catgId) {
            case WatchlistCategoryType.PaymentDefault:
                cureTypes = [WatchlistCureType.BroughtCurrent];
                break;
            case WatchlistCategoryType.MaturityDefault:
                cureTypes = [WatchlistCureType.Extended, WatchlistCureType.Repaid];
                break;
            case WatchlistCategoryType.ExtendedInterestDeferral:
                cureTypes = [WatchlistCureType.RepaidAllInterestAndFees];
                break;
            case WatchlistCategoryType.UnapprovedLiens:
                cureTypes = [WatchlistCureType.NoUnapprovedLiens];
                break;
            case WatchlistCategoryType.TitleIssue:
                cureTypes = [WatchlistCureType.ClearTitle];
                break;
            case WatchlistCategoryType.Litigation:
                cureTypes = [WatchlistCureType.LitigationResolved];
                break;
            case WatchlistCategoryType.OutstandingOver24Months:
                cureTypes = [WatchlistCureType.LoanRepayment];
                break;
            case WatchlistCategoryType.CompletionLessThan50Percentat180Days:
            case WatchlistCategoryType.CompletionLessThan75PercentAt1Year:
                cureTypes = [WatchlistCureType.Complet95PercentOrCertificateOfOccupany];
                break;
            case WatchlistCategoryType.Other:
                cureTypes = [WatchlistCureType.Other];
                break;
            default:
                cureTypes = [];
                break;
        }

        const cures = [];
        cureTypes.forEach((c) => {
            var option = WatchlistCureTypeOptions.find((a) => a.id === c);
            if (option) {
                cures.push({ ...option });
            }
        });
        return cures;
    };

    const onCategoryTypeChange = (catgId) => {
        set_cureTypeId(null);
        set_curedDate('');
        set_otherCureName('');
        set_isOutstanding(false);
        set_closedDate(null);
        set_categoryTypeId(catgId);
    };

    const onCureTypeChange = (cureId) => {
        if (!cureId) {
            set_cureTypeId(null);
            set_curedDate('');
            set_otherCureName('');
            set_isOutstanding(false);
            set_closedDate(null);
        } else {
            set_cureTypeId(cureId);
        }
    };

    const isLocked = () => {
        return item.isLocked || !hasPermission(PermissionType.WatchlistEdit);
    };

    const onIsOutstandingChanged = (value) => {
        set_isOutstanding(value);
        if (value) {
            set_closedDate(null);
        }
    };

    const onIssueDateChanged = (value) => {
        if (!value) {
            set_issueDate(null);
            return;
        }

        var dt = createDate(value);
        if (curedDate) {
            if (createDate(curedDate) < dt) {
                set_curedDate(null);
            }
        }
        if (closedDate) {
            if (createDate(closedDate) < dt) {
                set_closedDate(null);
            }
        }
        set_issueDate(dt);
    };

    const onCuredDateChanged = (value) => {
        if (!value) {
            set_curedDate(null);
            return;
        }
        if (closedDate) {
            if (createDate(closedDate) < dt) {
                set_closedDate(null);
            }
        }
        var dt = createDate(value);
        set_curedDate(dt);
    };

    const onClosedDateChanged = (value) => {
        if (!value) {
            set_closedDate(null);
            return;
        }

        var dt = createDate(value);
        set_closedDate(dt);
    };

    const getMinimumCuredDate = () => {
        if (!issueDate) {
            return null;
        }
        return issueDate;
    };

    if (!item) {
        return;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={true}
                onClose={handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '600px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <div
                                    className='page-header'
                                    role='banner'
                                >
                                    <h1 style={{ whiteSpace: 'nowrap' }}>{title}</h1>
                                    <button
                                        type='button'
                                        className='close'
                                        data-dismiss='modal'
                                        aria-label='Close'
                                        onClick={handleClose}
                                    >
                                        <span aria-hidden='true'>&times;</span>
                                    </button>
                                </div>
                                <form
                                    name='issueForm'
                                    onSubmit={onFormSubmit}
                                >
                                    <div
                                        style={styles.form}
                                        className={'standard-form'}
                                    >
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='issueDate'>
                                                    Issue Date <span className='req'>*</span>
                                                </label>
                                                <DateBox
                                                    id={'issueDate'}
                                                    value={item.issueDate}
                                                    required={true}
                                                    onValueChanged={(e) => onIssueDateChanged(e.value)}
                                                    disabled={isLocked()}
                                                />
                                            </div>
                                        </div>
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='categoryTypeId'>
                                                    Category <span className='req'>*</span>
                                                </label>
                                                <select
                                                    id='categoryTypeId'
                                                    value={categoryTypeId}
                                                    onChange={(event) => onCategoryTypeChange(parseInt(event.target.value))}
                                                    required={true}
                                                    disabled={isLocked()}
                                                >
                                                    {allowedCategories.map((fs) => {
                                                        return (
                                                            <option
                                                                key={fs.id}
                                                                value={fs.id}
                                                            >
                                                                {fs.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                        {categoryTypeId === WatchlistCategoryType.Other && (
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='otherCategoryName'>
                                                        Category Name
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        value={otherCategoryName}
                                                        onChange={(e) => set_otherCategoryName(e.target.value)}
                                                        required={true}
                                                        maxLength={100}
                                                        disabled={isLocked()}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        <div
                                            className='row'
                                            style={styles.formRow}
                                        >
                                            <div className='columns small-12 large-6'>
                                                <label htmlFor='cureTypeId'>Cure</label>
                                                <select
                                                    id='cureTypeId'
                                                    value={cureTypeId}
                                                    onChange={(event) => onCureTypeChange(parseInt(event.target.value))}
                                                    required={false}
                                                    disabled={isLocked()}
                                                >
                                                    <option value=''>Not Cured</option>
                                                    {getAllowedCures(categoryTypeId).map((fs) => {
                                                        return (
                                                            <option
                                                                key={fs.id}
                                                                value={fs.id}
                                                            >
                                                                {fs.name}
                                                            </option>
                                                        );
                                                    })}
                                                </select>
                                            </div>
                                        </div>
                                        {cureTypeId === WatchlistCureType.Other && (
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-12'>
                                                    <label htmlFor='otherCureName'>
                                                        Cure Name
                                                        <span className='req'>*</span>
                                                    </label>
                                                    <input
                                                        type='text'
                                                        value={otherCureName}
                                                        onChange={(e) => set_otherCureName(e.target.value)}
                                                        required={true}
                                                        maxLength={100}
                                                        disabled={isLocked()}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                        {cureTypeId && (
                                            <div
                                                className='row'
                                                style={styles.formRow}
                                            >
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='curedDate'>
                                                        Cured Date <span className='req'>*</span>
                                                    </label>
                                                    <DateBox
                                                        id={'curedDate'}
                                                        value={curedDate}
                                                        required={true}
                                                        min={getMinimumCuredDate()}
                                                        onValueChange={onCuredDateChanged}
                                                        disabled={isLocked()}
                                                    />
                                                </div>
                                                <div
                                                    className='columns small-12 large-4'
                                                    style={{
                                                        paddingTop: '25px',
                                                        fontSize: '0.75rem',
                                                        fontWeight: '500',
                                                    }}
                                                >
                                                    <input
                                                        type='checkbox'
                                                        id={'isOutstanding'}
                                                        checked={isOutstanding}
                                                        onChange={(e) => onIsOutstandingChanged(e.target.checked)}
                                                        disabled={isLocked()}
                                                    />
                                                    &nbsp; Still Outstanding?
                                                </div>
                                                <div className='columns small-12 large-4'>
                                                    <label htmlFor='curedDate'>
                                                        Closed Date <span className='req'>*</span>
                                                    </label>
                                                    <DateBox
                                                        id={'closedDate'}
                                                        value={closedDate}
                                                        required={true}
                                                        min={new Date(item.curedDate) ?? new Date(item.issueDate)}
                                                        onValueChanged={(e) => onClosedDateChanged(e.value)}
                                                        disabled={isLocked() || isOutstanding}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div
                                        className='button-footer'
                                        style={styles.buttonContainer}
                                    >
                                        <button
                                            type='submit'
                                            className='button primary'
                                            data-dismiss='modal'
                                            disabled={!canSubmit()}
                                        >
                                            Submit
                                        </button>
                                        &nbsp;
                                        <button
                                            type='button'
                                            className='button hollow'
                                            data-dismiss='modal'
                                            onClick={handleClose}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
};
